import { useState, useEffect } from "react";

interface ProfileFieldBalancedProps {
  label: string;
  value?: string;
  isRequired: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (value: string) => void;
  onCancel: () => void;
  placeholder?: string;
  saveStatus?: 'idle' | 'saving' | 'saved' | 'error';
}

const isValidUrl = (string: string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

export function ProfileFieldBalanced({
  label,
  value,
  isRequired,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  placeholder,
  saveStatus = 'idle'
}: ProfileFieldBalancedProps) {
  const [editValue, setEditValue] = useState(value || '');
  
  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleSave = () => {
    if (isRequired && !editValue.trim()) {
      alert(`${label} is required`);
      return;
    }
    
    if (label === 'Website' && editValue && !isValidUrl(editValue)) {
      alert('Please enter a valid website URL starting with http:// or https://');
      return;
    }
    
    onSave(editValue);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      onCancel();
    }
  };

  const getSaveStatusText = () => {
    switch (saveStatus) {
      case 'saving':
        return <span className="status-text saving">Saving...</span>;
      case 'saved':
        return <span className="status-text saved">Saved</span>;
      case 'error':
        return <span className="status-text error">Error</span>;
      default:
        return null;
    }
  };

  return (
    <div className="profile-field-balanced">
      <div className="field-label-container-balanced">
        <label className="field-label-balanced">
          {label}
          {isRequired && <span className="required-indicator">*</span>}
        </label>
        {getSaveStatusText()}
      </div>

      <div className="field-container-balanced">
        {isEditing ? (
          <div className="edit-mode-balanced">
            <input
              type={label === 'Website' ? 'url' : 'text'}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={placeholder}
              className="field-input-balanced"
              autoFocus
              disabled={saveStatus === 'saving'}
            />
            <div className="edit-actions-balanced">
              <button 
                onClick={handleSave} 
                className="save-button-balanced"
                disabled={saveStatus === 'saving'}
              >
                Save
              </button>
              <button 
                onClick={onCancel} 
                className="cancel-button-balanced"
                disabled={saveStatus === 'saving'}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="field-display-container-balanced">
            <div 
              className="field-display-box-balanced"
              onClick={onEdit}
            >
              {value ? (
                <span className="field-value-text">{value}</span>
              ) : (
                <span className="field-placeholder-text">{placeholder}</span>
              )}
            </div>
            <button 
              className="edit-action-balanced"
              onClick={onEdit}
            >
              {value ? 'Edit' : 'Add'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}